<template>
  <div
    v-if="user.teachable_id && user.teachable_id !== '' && qaAreaShow"
    class="container py-4 hexschool-q-a"
  >
    <Toasts />
    <Notification v-if="token && listStatus" :message-id="notificationParams" />
    <WeekendModal
      v-if="isWeekendModalShow"
      @close="closeWeekendModal"
    />
    <NotifyModal
      v-if="isNotifyModalShow"
      @close="closeNotifyModal"
      @turn-off="notifyNotShowAgain"
    />
    <keep-alive>
      <QusList
        v-if="viewNow === 'list'"
        :token="token"
        :refresh="refreshStatus"
        :is-link="isLink"
        @changeView="changeView"
        @get-ok="getListOk"
      />
    </keep-alive>
    <AskNewQus
      v-if="viewNow === 'new'"
      @changeView="changeView"
    />
    <QusDetail
      v-if="viewNow === 'detail'"
      :id="questionId"
      :token="token"
      :is-link="isLink"
      @changeView="changeView"
    />
    <!-- <HelloWorld msg="Hello Vue 3 + Vite" /> -->
  </div>
</template>

<script setup>
/**
 * 問答區根元件 (App.vue)
 *
 * Teachable 課程頁內嵌問答區的進入點，負責：
 * - 讀取 Teachable 使用者資訊（`#fedora-data`）並登入取得 token
 * - 依網址參數處理 RPG 綁定導轉、通知訊息與問題深連結
 * - 在 list / new / detail 三種畫面間切換，並掛載假日、改版通知 Modal
 */
import { ref, onMounted } from 'vue';
import authAPI from './apis/auth';
import QusDetail from './components/QusDetail';
import AskNewQus from './components/AskNewQus';
import QusList from './components/QusList';
import WeekendModal from './components/WeekendModal';
import NotifyModal from './components/NotifyModal.vue';
import Notification from './components/Notification';
import Toasts from './components/Toasts';
import { getCookie } from './utils/cookie';
import { useToast } from './composables/useToast';
import { useSession } from './composables/useSession';
import { directToRPGUserSystem, refreshToken, saveTokenToCookie } from './services/auth';
import entryAPI from './apis/entry';

const { postMessage } = useToast();
const { qaAreaShow, checkActionPermission } = useSession();

// 問答列表取得成功才顯示通知
const listStatus = ref(false);
const getListOk = () => {
  listStatus.value = true;
};

// 假日 / 助教休息中 Modal 顯示
const isWeekendModalShow = ref(false);
/**
 * 判斷今天是否為週末，且今日尚未顯示過假日 Modal，據此設定 `isWeekendModalShow`。
 */
const isWeekend = () => { // 確認是否是假日
  const dt = new Date();
  const dtMonth = dt.getMonth();
  const dtDay = dt.getDate();
  const isWeeekend = (dt.getDay() === 6 || dt.getDay() === 0);
  const weekendData = JSON.parse(window.localStorage.getItem('h_weekend_notice')) || [];
  const today = (new Date(weekendData.date).getMonth() !== dtMonth || new Date(weekendData.date).getDate() !== dtDay);
  isWeekendModalShow.value = isWeeekend && today; // 今天是假日，且沒有儲存過資料
};
isWeekend();
/**
 * 關閉假日 Modal，並將「今日已顯示」狀態寫入 localStorage（`h_weekend_notice`）。
 * @param {boolean} value - Modal 的顯示狀態（傳入 false 關閉）
 */
const closeWeekendModal = (value) => {
  isWeekendModalShow.value = value;
  const modalStatus = {
    show: false,
    date: new Date().getTime(),
  };
  window.localStorage.setItem('h_weekend_notice', JSON.stringify(modalStatus));
};

// 通知系統更新 modal
const isNotifyModalShow = ref(false);
/**
 * 判斷今日是否尚未顯示過改版通知 Modal，據此設定 `isNotifyModalShow`。
 */
const isNotify = () => { //
  const dt = new Date();
  const dtMonth = dt.getMonth();
  const dtDay = dt.getDate();
  // 今日不再顯示
  const notifyStatus = JSON.parse(window.localStorage.getItem('h_system_notice')) || [];
  const today = (new Date(notifyStatus.date).getMonth() !== dtMonth || new Date(notifyStatus.date).getDate() !== dtDay);
  isNotifyModalShow.value = today;
};
const closeNotifyModal = (value) => {
  isNotifyModalShow.value = value;
};
/**
 * 勾選「今天不再顯示」改版通知時，將狀態寫入 localStorage（`h_system_notice`）。
 * @param {boolean} check - 是否勾選不再顯示
 */
const notifyNotShowAgain = (check) => {
  if (check) {
     const modalStatus = {
    show: false,
    date: new Date().getTime(),
  };
  window.localStorage.setItem('h_system_notice', JSON.stringify(modalStatus));
  }
 
};

// 登入相關
const isLink = ref(false); // 是否綁定 RPG（is_link）
const token = ref('');
const user = ref({
  teachable_id: '',
  email: '',
});
/**
 * 以 Teachable 使用者資訊呼叫登入 API。
 * 未綁定 RPG 時直接使用 teachable token；已綁定則導向 RPG 使用者系統
 * （或於本機環境直接寫入 cookie）。成功後若網址帶有 `questions_id` 則開啟該問題詳情。
 */
const login = () => {
  authAPI.apiPostLogin({
    teachable_id: user.value.teachable_id || '',
    email: user.value.email || '',
  }).then((res) => {
    const tokenExp = res.data.expiration;
    /* API 回傳的 access_token token 是 teachable 暫時的 token，不是 RPG 的 token
       如果已經綁定 RPG，則需要導向 RPG 登入頁面並 redirect 回來
    */
    isLink.value = res.data.is_link;
    if (!res.data.is_link) { // 未綁定過，直接使用 teachable token
      saveTokenToCookie(tokenExp, res.data.access_token, 'teachable', 'false');
      token.value = res.data.access_token;
    } else {
      // 已綁定過 
      if (['production', 'development'].includes(process.env.VUE_APP_ENV)) {
        directToRPGUserSystem(true);
      } else {
        saveTokenToCookie(tokenExp, res.data.access_token, 'teachable', 'true');
        token.value = res.data.access_token;
      }
    }
      
    if (questionParams.value) {
      changeView({type: 'detail', id: questionParams.value, refresh: false})
    }
  }).catch((error) => {
    postMessage('danger', '取得使用者發生錯誤', `${error?.response?.status} - ${error?.response?.data.message}`);
  });
};
onMounted(() => {
  const userInfo = document.querySelector('#fedora-data');
  questionParams.value = new URL(location.href).searchParams.get('questions_id');
  notificationParams.value = new URL(location.href).searchParams.get('message_id');
  // 串接回來的訊息
  // https://courses.hexschool.com/courses/bc2694/lectures/40189606?msg=成功&state=success&access_token=token&exp=過期時間
  const paramsMsg = new URL(location.href).searchParams.get('msg');
  const paramsType = new URL(location.href).searchParams.get('state');
  if (paramsType && paramsMsg) {
    const mapState = {
      success: {
        type: 'success',
        title: '成功',
      },
      error: {
        msgType: 'danger',
        title: '失敗',
      },
    }
    postMessage(mapState[paramsType]?.type, mapState[paramsType]?.title, paramsMsg)
    if (paramsType === 'success') {
      const paramsToken = new URL(location.href).searchParams.get('access_token');
      const expiration = new URL(location.href).searchParams.get('exp');
      saveTokenToCookie(expiration, paramsToken, 'rpg', 'true');
      token.value = paramsToken;
    }
  }

  if (userInfo && userInfo.dataset.id) {
    user.value.teachable_id = userInfo.dataset.id;
    user.value.email = userInfo.dataset.email;
    isLink.value = getCookie('rpg_temp_token_is_link') === 'true';
    const tokenInCookie = getCookie('rpg_temp_token');
    checkActionPermission();
    if (tokenInCookie && tokenInCookie !== "") {
      // Authorization 由各 Request 實例的 request 攔截器讀 cookie 自動帶入，這裡不再設定全域 header
      token.value = tokenInCookie;
      isLink.value = getCookie('rpg_temp_token_is_link') === 'true';
      // 進畫面以 entry 探測入場權限；token 過期時由 interceptor 自動 refresh 換發後重試（見 apis/core/interceptors.js）
      entryAPI.apiGetEntryPermission();

      if (!isLink.value) { // 沒綁定過的話，顯示改版通知
        isNotify();
      }
      if (questionParams.value) {
        changeView({type: 'detail', id: questionParams.value, refresh: false})
      }
    } else if (getCookie('rpg_temp_token_type')) {
      // access token cookie 已過期被瀏覽器刪除，但曾登入過（type / is_link 仍在）、httpOnly refresh_token
      // 可能仍有效 → 先用 refresh token 換發新 access token，成功才算已登入；真的失效才走 teachable 登入
      refreshToken().then((newToken) => {
        if (newToken) {
          token.value = getCookie('rpg_temp_token');
          isLink.value = getCookie('rpg_temp_token_is_link') === 'true';
          checkActionPermission();
          entryAPI.apiGetEntryPermission();
          if (!isLink.value) { // 沒綁定過的話，顯示改版通知
            isNotify();
          }
          if (questionParams.value) {
            changeView({ type: 'detail', id: questionParams.value, refresh: false });
          }
        } else {
          login(); // refresh token 也失效 → 重新登入
        }
      }).catch(() => {
        login();
      });
    } else {
      login(); // 全新訪客，沒有可用的 session
    }
  }

  // 錯誤處理
  const errorParams = new URL(location.href).searchParams.get('errorType');
  const errorMsg = new URL(location.href).searchParams.get('errorMsg');
  if (errorParams && errorMsg) {
    postMessage('danger', '登入 RPG 帳號失敗', `${errorMsg}`);
  }
});

// 當前看到的畫面
const viewNow = ref('list'); // 當前看到的畫面：問題列表(list)、問題詳細(detail)、新問題(new)
const refreshStatus = ref(false); // 當前看到的畫面：問題列表(list)、問題詳細(detail)、新問題(new)
const questionParams = ref(null); // questions_id
const notificationParams = ref(null); // message_id
const questionId = ref(-1); // 當前看到的畫面：問題列表(list)、問題詳細(detail)、新問題(new)
/**
 * 切換當前顯示的畫面。
 * @param {{ type: 'list'|'new'|'detail', id: number, refresh: boolean }} payload - 目標畫面、問題 id 與是否重新整理列表
 */
const changeView = ({ type, id, refresh }) => {
  viewNow.value = type;
  questionId.value = id;
  refreshStatus.value = refresh;
};
</script>

<style lang="scss">
@import './assets/scss/all.scss';
</style>

<spec lang="md">
# 問答區根元件 (App.vue)

## 元件概述
- **用途**：Teachable 課程頁內嵌問答區的根元件，負責使用者登入、畫面切換（問題列表／新增問題／問題詳情）與各式通知 Modal 的掛載。

## Props
無

## Emits
無

## Slots
無

## 功能行為
- 僅在取得 Teachable 使用者（`user.teachable_id` 不為空）且 `qaAreaShow` 為 `true` 時渲染問答區
- `onMounted` 時讀取 `#fedora-data` 的 `data-id` / `data-email` 作為使用者資訊
- 解析網址參數：`questions_id`（深連結至問題詳情）、`message_id`（通知）、`msg`/`state`（RPG 導轉回來的結果訊息）、`access_token`/`exp`（寫入 cookie）、`errorType`/`errorMsg`（錯誤提示）
- cookie 內已有 `rpg_temp_token` 時直接使用並以 entry API（`apiGetEntryPermission`）探測入場權限；token 過期時由 interceptor 自動 refresh 換發後重試，未綁定者顯示改版通知
- cookie 無 `rpg_temp_token`（access token 過期被刪）但仍有 `rpg_temp_token_type`（曾登入過）時，先呼叫 `refreshToken()` 以 httpOnly refresh token 換發新 access token，成功則比照已登入初始化，失敗才 `login()`
- 完全無 token 相關 cookie（全新訪客）時才直接呼叫 `login()`：未綁定 RPG 使用 teachable token；已綁定則導向 RPG 使用者系統
- `viewNow` 控制顯示 `QusList` / `AskNewQus` / `QusDetail`，其中 `QusList` 以 `<keep-alive>` 快取
- 週末且今日未顯示過時顯示 `WeekendModal`；今日未顯示過改版通知時顯示 `NotifyModal`

## 測試行為
- `user.teachable_id` 為空或 `qaAreaShow` 為 `false` 時，不應渲染問答區
- `viewNow` 為 `list` 時，應顯示 `QusList`
- `viewNow` 為 `new` 時，應顯示 `AskNewQus`
- `viewNow` 為 `detail` 時，應顯示 `QusDetail` 並帶入 `questionId`
- `token` 與 `listStatus` 皆為 `true` 時，應顯示 `Notification`
- 網址帶 `state=success` 與 `msg` 時，應呼叫 `postMessage` 並將 token 寫入 cookie
- cookie 有 `rpg_temp_token` 時，應呼叫 `checkActionPermission` 與 entry API（`apiGetEntryPermission`）
- 無 `rpg_temp_token` 但有 `rpg_temp_token_type` 時，應先呼叫 `refreshToken()`，失敗才 `login()`
- 完全無 token 相關 cookie 時，應直接呼叫 `login()`
- `changeView` 被呼叫時，應更新 `viewNow` / `questionId` / `refreshStatus`
- `isWeekend()` 為週末且今日未顯示過時，應顯示 `WeekendModal`

## UI 規格
- 根容器套用 `.container.py-4.hexschool-q-a`
- 全域樣式由 `@import './assets/scss/all.scss'`（非 scoped）提供
- `QusList` 以 `<keep-alive>` 包裹以保留列表狀態
</spec>

